import { MobileSectionDivider } from "./mobile-section-divider";
import { MobileStoryCardElevated } from "./mobile-story-card-elevated";
import { MobileStoryCompactPair } from "./mobile-story-compact-pair";
import { MobileStoryFullBleedCard } from "./mobile-story-full-bleed-card";
import { MobileStoryGridQuad } from "./mobile-story-grid-quad";
import { MobileStoryHeadlineCard } from "./mobile-story-headline-card";
import { MobileStoryHeroCard } from "./mobile-story-hero-card";
import { MobileStoryImageCard } from "./mobile-story-image-card";
import type { MobileSlot } from "./mobile-story-rhythm";
import { MobileStoryTextCard } from "./mobile-story-text-card";
import { MobileStoryTextOnlyCard } from "./mobile-story-text-only-card";
import { MobileStoryTinyListCard } from "./mobile-story-tiny-list-card";

interface MobileSlotRendererProps {
  slot: MobileSlot;
  exploreBase: string;
  priorityStoryId?: string;
}

/**
 * Renders a single mobile layout slot using the correct card component.
 * Keeps section components free of switch/case logic.
 */
export function MobileSlotRenderer({
  slot,
  exploreBase,
  priorityStoryId,
}: MobileSlotRendererProps) {
  switch (slot.variant) {
    case "hero":
      return (
        <MobileStoryHeroCard
          exploreBase={exploreBase}
          priority={slot.story.storyId === priorityStoryId}
          story={slot.story}
        />
      );
    case "image":
      return (
        <MobileStoryImageCard
          exploreBase={exploreBase}
          priority={slot.story.storyId === priorityStoryId}
          story={slot.story}
        />
      );
    case "headline":
      return (
        <MobileStoryHeadlineCard
          exploreBase={exploreBase}
          priority={slot.story.storyId === priorityStoryId}
          story={slot.story}
        />
      );
    case "full-bleed":
      return (
        <MobileStoryFullBleedCard
          exploreBase={exploreBase}
          priority={slot.story.storyId === priorityStoryId}
          story={slot.story}
        />
      );
    case "elevated":
      return (
        <MobileStoryCardElevated exploreBase={exploreBase} story={slot.story} />
      );
    case "text":
      return (
        <MobileStoryTextCard
          exploreBase={exploreBase}
          showDivider={slot.showDivider}
          story={slot.story}
        />
      );
    case "compact-pair":
      return (
        <MobileStoryCompactPair
          exploreBase={exploreBase}
          stories={slot.stories}
        />
      );
    case "grid-quad":
      return (
        <MobileStoryGridQuad exploreBase={exploreBase} stories={slot.stories} />
      );
    case "text-only":
      return (
        <MobileStoryTextOnlyCard
          exploreBase={exploreBase}
          showDivider={slot.showDivider}
          story={slot.story}
        />
      );
    case "tiny-list":
      return (
        <MobileStoryTinyListCard
          exploreBase={exploreBase}
          showDivider={slot.showDivider}
          story={slot.story}
        />
      );
    case "section-divider":
      return <MobileSectionDivider title={slot.title} />;
    default:
      break;
  }
}

/** Stable key for a mobile slot. */
export function slotKey({
  slot,
  index,
}: {
  slot: MobileSlot;
  index: number;
}): string {
  if (slot.variant === "compact-pair" || slot.variant === "grid-quad") {
    return `${slot.variant}-${slot.stories.map((s) => s.storyId).join("-")}`;
  }
  if (slot.variant === "section-divider") {
    return `divider-${slot.title}-${index}`;
  }
  return `${slot.variant}-${slot.story.storyId}-${index}`;
}
